import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  CircularProgress,
  Typography,
  Box,
  Paper,
  LinearProgress,
  IconButton,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SyncIcon from "@material-ui/icons/Sync";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CloseIcon from "@material-ui/icons/Close";

import { AuthContext } from "../../context/Auth/AuthContext";

const PREPARING_TIMEOUT = 30000; // 30s sem progresso em "preparing" → fecha

const useStyles = makeStyles((theme) => ({
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: 16,
      overflow: "hidden",
      minWidth: 340,
      maxWidth: 400,
    },
  },
  paper: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "#fff",
    position: "relative",
  },
  paperCompleted: {
    background: "linear-gradient(135deg, #43a047 0%, #66bb6a 100%)",
  },
  closeButton: {
    position: "absolute",
    top: 4,
    right: 4,
    color: "rgba(255, 255, 255, 0.7)",
    "&:hover": {
      color: "#fff",
    },
  },
  iconWrapper: {
    position: "relative",
    marginBottom: theme.spacing(2),
    width: 70,
    height: 70,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  spinner: {
    position: "absolute",
    color: "#fff",
  },
  syncIcon: {
    fontSize: 30,
    color: "#fff",
    animation: "$spin 2s linear infinite",
  },
  downloadIcon: {
    fontSize: 30,
    color: "#fff",
  },
  completedIcon: {
    fontSize: 40,
    color: "#fff",
  },
  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },
  title: {
    fontWeight: 600,
    fontSize: "1.2rem",
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    fontSize: "0.9rem",
    opacity: 0.9,
    marginBottom: theme.spacing(2),
  },
  progressBar: {
    width: "100%",
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    "& .MuiLinearProgress-bar": {
      borderRadius: 4,
      backgroundColor: "#fff",
    },
  },
  statsText: {
    fontSize: "1.5rem",
    fontWeight: 700,
    marginTop: theme.spacing(1),
  },
  detailText: {
    fontSize: "0.85rem",
    opacity: 0.8,
    marginTop: theme.spacing(0.5),
  },
}));

const SyncStatusOverlay = () => {
  const classes = useStyles();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;

  const [isOpen, setIsOpen] = useState(false);
  const [phase, setPhase] = useState(null); // "receiving" | "preparing" | "importing" | "completed"
  const [syncProgress, setSyncProgress] = useState(null);
  const [accumulated, setAccumulated] = useState(0);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(0);
  const [created, setCreated] = useState(0);
  const [processingStarted, setProcessingStarted] = useState(false);
  const preparingTimerRef = useRef(null);

  const closeOverlay = () => {
    setIsOpen(false);
    setPhase(null);
    setSyncProgress(null);
    setAccumulated(0);
    setCurrent(0);
    setTotal(0);
    setCreated(0);
    setProcessingStarted(false);
    if (preparingTimerRef.current) {
      clearTimeout(preparingTimerRef.current);
      preparingTimerRef.current = null;
    }
  };

  useEffect(() => {
    if (!socket || !companyId) return;

    const eventName = `importMessages-${companyId}`;

    const handleProgress = (data) => {
      if (data.action === "refresh") {
        closeOverlay();
        return;
      }

      if (data.action !== "update" || !data.status) return;

      const status = data.status;

      // Fase de recebimento (this === -1)
      if (status.this === -1) {
        if (processingStarted) return;

        // Limpar timeout de preparing se receber novo evento
        if (preparingTimerRef.current) {
          clearTimeout(preparingTimerRef.current);
          preparingTimerRef.current = null;
        }

        if (status.accumulated != null && status.accumulated > 0) {
          setAccumulated(status.accumulated);
          if (status.progress != null) setSyncProgress(status.progress);
          setPhase("receiving");
        } else {
          setPhase("preparing");
          // Timeout: se ficar em "preparing" sem progresso, fecha sozinho
          preparingTimerRef.current = setTimeout(() => {
            closeOverlay();
          }, PREPARING_TIMEOUT);
        }
        setIsOpen(true);
        return;
      }

      // Fase de processamento (this >= 0)
      if (preparingTimerRef.current) {
        clearTimeout(preparingTimerRef.current);
        preparingTimerRef.current = null;
      }
      if (!processingStarted) setProcessingStarted(true);

      if (status.completed) {
        setPhase("completed");
        setCurrent(status.this);
        setTotal(status.all);
        setCreated(status.created || 0);
        setTimeout(() => {
          closeOverlay();
        }, 4000);
        return;
      }

      setPhase("importing");
      setCurrent(status.this);
      setTotal(status.all);
      setCreated(status.created || 0);
      setIsOpen(true);
    };

    socket.on(eventName, handleProgress);

    return () => {
      socket.off(eventName, handleProgress);
      if (preparingTimerRef.current) {
        clearTimeout(preparingTimerRef.current);
        preparingTimerRef.current = null;
      }
    };
  }, [socket, companyId, processingStarted]);

  if (!isOpen || !phase) {
    return null;
  }

  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  const estimatedTotal = syncProgress > 0 ? Math.round(accumulated / (syncProgress / 100)) : null;

  const renderContent = () => {
    switch (phase) {
      case "receiving":
        return (
          <>
            <Box className={classes.iconWrapper}>
              <CircularProgress
                size={70}
                thickness={2}
                className={classes.spinner}
              />
              <SyncIcon className={classes.syncIcon} />
            </Box>
            <Typography className={classes.title}>
              Recebendo historico
            </Typography>
            <Typography className={classes.subtitle}>
              Baixando mensagens do WhatsApp...
            </Typography>
            <Typography className={classes.statsText}>
              {accumulated}
              {estimatedTotal ? ` / ~${estimatedTotal}` : ""}
            </Typography>
            <Typography className={classes.detailText}>
              mensagens recebidas
            </Typography>
            {syncProgress != null && (
              <LinearProgress
                variant="determinate"
                value={syncProgress}
                className={classes.progressBar}
              />
            )}
          </>
        );

      case "preparing":
        return (
          <>
            <Box className={classes.iconWrapper}>
              <CircularProgress
                size={70}
                thickness={2}
                className={classes.spinner}
              />
              <SyncIcon className={classes.syncIcon} />
            </Box>
            <Typography className={classes.title}>
              Preparando importacao
            </Typography>
            <Typography className={classes.subtitle}>
              Aguarde, preparando mensagens para processamento...
            </Typography>
            <LinearProgress
              variant="indeterminate"
              className={classes.progressBar}
            />
          </>
        );

      case "importing":
        return (
          <>
            <Box className={classes.iconWrapper}>
              <CircularProgress
                size={70}
                thickness={2}
                className={classes.spinner}
              />
              <CloudDownloadIcon className={classes.downloadIcon} />
            </Box>
            <Typography className={classes.title}>
              Importando mensagens
            </Typography>
            <Typography className={classes.subtitle}>
              Processando historico de mensagens...
            </Typography>
            <Typography className={classes.statsText}>
              {current} / {total}
            </Typography>
            <Typography className={classes.detailText}>
              {created > 0 ? `${created} criadas` : ""}{" "}
              {percentage}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={percentage}
              className={classes.progressBar}
            />
          </>
        );

      case "completed":
        return (
          <>
            <Box className={classes.iconWrapper}>
              <CheckCircleIcon className={classes.completedIcon} />
            </Box>
            <Typography className={classes.title}>
              Importacao concluida!
            </Typography>
            <Typography className={classes.subtitle}>
              {created} mensagens importadas de {total}
            </Typography>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={true}
      className={classes.dialog}
      disableBackdropClick
      disableEscapeKeyDown
      BackdropProps={{
        style: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
      }}
    >
      <Paper
        className={`${classes.paper} ${phase === "completed" ? classes.paperCompleted : ""}`}
        elevation={0}
      >
        <IconButton
          className={classes.closeButton}
          onClick={closeOverlay}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {renderContent()}
      </Paper>
    </Dialog>
  );
};

export default SyncStatusOverlay;
